import { formatRestrictionPlacement } from "../domain/restrictionPlacement";
import type { AcceptedRestriction, RestrictionCondition } from "../domain/studioTypes";

/** Saved rows must be plain ASCII: raw UTF-8 in a BOM-less CSV reads as mojibake. */
const toAscii = (value: string): string =>
  value
    // Badges BEFORE the NFKD fold: NFKD turns "™" into the letters "TM".
    .replace(/[®™©]/g, "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[‐-―−]/g, "-")
    .replace(/[‘’‛]/g, "'")
    .replace(/[“”‟]/g, '"')
    .replace(/×/g, "x")
    .replace(/[^\x20-\x7E]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const escapeCsv = (value: string): string => {
  const text = toAscii(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** @deprecated Underscored conditions cannot be uploaded; kept for tests. */
export const toRestrictionsUploadCsv = (restrictions: readonly AcceptedRestriction[]): string => {
  const header = ["variant_group_id", "variant", "bay_no", "shelf", "condition"];
  const rows: string[] = [];
  let group = 0;
  for (const restriction of restrictions) {
    const variant = restriction.cdtPath[restriction.cdtPath.length - 1]?.trim();
    const placement = restriction.placement;
    const cells = placement?.cells.filter((cell) => cell.bays.length > 0) ?? [];
    if (!variant || cells.length === 0) continue;
    group += 1;
    const condition =
      restriction.kind === "split-off" || restriction.kind === "breakout"
        ? "CAN_HAVE"
        : "MUST_HAVE";
    for (const cell of cells) {
      rows.push(
        [`CDT-STUDIO-${group}`, variant, cell.bays.join(" | "), String(cell.shelf), condition]
          .map(escapeCsv)
          .join(","),
      );
    }
  }
  return [header.join(","), ...rows].join("\n");
};

export const toRestrictionsDetailCsv = (restrictions: readonly AcceptedRestriction[]): string => {
  const header = [
    "id",
    "kind",
    "cdt_path",
    "collapsed_key",
    "bay_no",
    "shelf",
    "placement",
    "uploaded",
    "sku_ids",
    "source",
    "flag_id",
    "text",
  ];
  const rows = restrictions.map((restriction) =>
    [
      restriction.id,
      restriction.kind,
      restriction.cdtPath.join(" > "),
      restriction.collapsedKey ?? "",
      restriction.placement?.cells.map((cell) => cell.bays.join("+")).join(" | ") ?? "",
      restriction.placement?.cells.map((cell) => cell.shelf).join(" | ") ?? "",
      formatRestrictionPlacement(restriction.placement),
      restriction.placement ? "yes" : "no",
      restriction.skuIds.join(" "),
      restriction.source.origin,
      restriction.source.flagId ?? "",
      restriction.text,
    ]
      .map(escapeCsv)
      .join(","),
  );
  return [header.join(","), ...rows].join("\n");
};

/** Unshipped variants match nothing yet empty every cell they name. */
export const toRemedyRestrictionsCsv = (
  rows: readonly {
    variantGroupId: string;
    variant: string;
    bayNo: string;
    shelf: string;
    condition: string;
  }[],
  shippedVariants: readonly string[],
): string => {
  const shipped = new Set(shippedVariants);
  const header = ["variant_group_id", "variant", "bay_no", "shelf", "condition"];
  const emitted = rows.filter(
    (row) =>
      shipped.has(row.variant) &&
      row.bayNo.trim() !== "" &&
      row.bayNo.trim() !== "*" &&
      row.shelf.trim() !== "" &&
      row.shelf.trim() !== "*",
  );
  return [
    header.join(","),
    ...emitted.map((row) =>
      [row.variantGroupId, row.variant, row.bayNo, row.shelf, row.condition]
        .map(escapeCsv)
        .join(","),
    ),
  ].join("\n");
};

/** Conditions with spaces — `convertCondition` rejects underscores. */
const NEWLINE = String.fromCharCode(10);

const CSV_CONDITION: Readonly<Record<string, string>> = {
  CAN_HAVE: "CAN HAVE",
  MUST_HAVE: "MUST HAVE",
  MUST_HAVE_ONLY: "MUST HAVE ONLY",
  MUST_NOT_HAVE: "MUST NOT HAVE",
};

export const VARIANT_RESTRICTION_CSV_COLUMNS = [
  "variant_group_id",
  "variant",
  "bay_no",
  "shelf",
  "condition",
  "store_code",
  "store_variant_group_id",
  "pog_group",
  "pog_group_variant_group",
  "size",
  "size_variant_group",
] as const;

export type VariantRestrictionFileResult =
  | { ok: true; csv: string; restrictionRows: number; storeGroupRows: number }
  | { ok: false; refusal: string };

/** Underscored conditions; spaces are applied only at file write. */
export const restrictionsToFileRows = (
  restrictions: readonly AcceptedRestriction[],
): {
  variantGroupId: string;
  variant: string;
  bayNo: string;
  shelf: string;
  condition: string;
}[] => {
  const rows = [];
  let group = 0;
  for (const restriction of restrictions) {
    const variant = restriction.cdtPath[restriction.cdtPath.length - 1]?.trim();
    const cells = restriction.placement?.cells.filter((cell) => cell.bays.length > 0) ?? [];
    if (!variant || cells.length === 0) continue;
    group += 1;
    // Kind covers two of four spellings; a fence must state its own.
    const condition: RestrictionCondition =
      restriction.condition ??
      (restriction.kind === "split-off" || restriction.kind === "breakout"
        ? "CAN_HAVE"
        : "MUST_HAVE");
    for (const cell of cells) {
      rows.push({
        variantGroupId: `CDT-STUDIO-${group}`,
        variant,
        bayNo: cell.bays.join(" | "),
        shelf: String(cell.shelf),
        condition,
      });
    }
  }
  return rows;
};

/** Uploading the result REPLACES the category; see `planVariantRestrictionSave`. */
export const toVariantRestrictionsFileCsv = (
  rows: readonly {
    variantGroupId: string;
    variant: string;
    bayNo: string;
    shelf: string;
    condition: string;
  }[],
  shippedVariants: readonly string[],
  storeCodes: readonly string[],
): VariantRestrictionFileResult => {
  if (rows.length === 0) return { ok: false, refusal: "no restriction rows to write" };
  if (storeCodes.length === 0) {
    return {
      ok: false,
      refusal:
        "no store codes: the CSV path derives its groups from the store-group rows, so a file " +
        "without them attaches every rule to nothing and throws on upload. Use the save " +
        "endpoint if the intended scope is every store in the category.",
    };
  }
  const shipped = new Set(shippedVariants);
  const phantom = rows.find((row) => !shipped.has(row.variant));
  if (phantom) {
    return {
      ok: false,
      refusal: `"${phantom.variant}" is not in the shipped CDT0 list, so it would match no candidate and empty every cell it names`,
    };
  }
  const badCondition = rows.find((row) => CSV_CONDITION[row.condition] === undefined);
  if (badCondition) {
    return {
      ok: false,
      refusal: `condition "${badCondition.condition}" is not one the CSV path accepts (convertCondition throws on it)`,
    };
  }
  const wildcard = rows.find(
    (row) =>
      row.bayNo.trim() === "" ||
      row.bayNo.trim() === "*" ||
      row.shelf.trim() === "" ||
      row.shelf.trim() === "*",
  );
  if (wildcard) {
    return { ok: false, refusal: "a row carries a wildcard or empty bay/shelf" };
  }

  const blank = (
    fields: Partial<Record<(typeof VARIANT_RESTRICTION_CSV_COLUMNS)[number], string>>,
  ) => VARIANT_RESTRICTION_CSV_COLUMNS.map((column) => escapeCsv(fields[column] ?? "")).join(",");

  const restrictionRows = rows.map((row) =>
    blank({
      variant_group_id: row.variantGroupId,
      variant: row.variant,
      bay_no: row.bayNo,
      shelf: row.shelf,
      condition: CSV_CONDITION[row.condition],
    }),
  );

  const groupIds = [...new Set(rows.map((row) => row.variantGroupId))];
  const storeGroupRows = groupIds.flatMap((groupId) =>
    storeCodes.map((storeCode) =>
      blank({ store_code: storeCode, store_variant_group_id: groupId }),
    ),
  );

  return {
    ok: true,
    csv: [VARIANT_RESTRICTION_CSV_COLUMNS.join(","), ...restrictionRows, ...storeGroupRows].join(
      NEWLINE,
    ),
    restrictionRows: restrictionRows.length,
    storeGroupRows: storeGroupRows.length,
  };
};
